import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';

const Dashboard = () => {
  const { user, loading, isAuthenticated, token } = useAuth();
  const [stats, setStats] = useState({
    total_issues: 0,
    open_issues: 0,
    in_progress_issues: 0,
    closed_issues: 0,
    high_priority_issues: 0,
    critical_priority_issues: 0,
    recent_activities: []
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Only fetch dashboard stats when user is authenticated, auth loading is complete, and token exists
    if (isAuthenticated && !loading && user && token) {
      console.log('Authentication complete, fetching dashboard stats...');
      console.log('Current auth state:', { isAuthenticated, loading, hasUser: !!user, hasToken: !!token });
      
      // Add a small delay to ensure axios headers are set
      setTimeout(() => {
        fetchDashboardStats();
      }, 100);
    } else {
      console.log('Auth not ready:', { isAuthenticated, loading, hasUser: !!user, hasToken: !!token });
    }
  }, [isAuthenticated, loading, user, token]);

  const fetchDashboardStats = async () => {
    try {
      setStatsLoading(true);
      setError('');
      
      // Double-check token before making request
      const currentToken = localStorage.getItem('token');
      if (!currentToken) {
        setError('No authentication token found');
        return;
      }
      
      console.log('Making dashboard stats request...');
      console.log('Request headers check:', axios.defaults.headers.common['Authorization'] ? 'Header set' : 'Header missing');
      
      const response = await axios.get('/dashboard/stats');
      setStats(response.data);
      console.log('Dashboard stats loaded successfully');
    } catch (error) {
      const errorMsg = 'Failed to load dashboard statistics';
      setError(errorMsg);
      console.error('Error fetching dashboard stats:', error);
      
      // If it's a 401 error, it might be a token issue
      if (error.response?.status === 401) {
        console.error('401 error - authentication failed. Token may be invalid or not properly set.');
      }
    } finally {
      setStatsLoading(false);
    }
  };

  const StatCard = ({ title, value, color, icon, link }) => (
    <div className={`stat-card stat-card-${color}`}>
      <div className="stat-card-content">
        <div className="stat-card-header">
          <span className="stat-card-icon">{icon}</span>
          <h3 className="stat-card-title">{title}</h3>
        </div>
        <div className="stat-card-value">{value}</div>
        {link && (
          <Link to={link} className="stat-card-link">
            View Details →
          </Link>
        )}
      </div>
    </div>
  );

  // Show loading while authentication is still in progress
  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Authenticating...</p>
      </div>
    );
  }

  // Show loading while dashboard stats are being fetched
  if (statsLoading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="dashboard enhanced-user-dashboard">
        <div className="dashboard-header enhanced-header">
          <div className="header-content">
            <h1>👋 Welcome back, {user?.first_name}!</h1>
            <p>Here's your personal project overview and activity</p>
          </div>
          <div className="user-role-badge">
            <span className={`role-badge role-${user?.role?.toLowerCase()}`}>
              {user?.role === 'Admin' ? '🛡️' : user?.role === 'Developer' ? '💻' : '🧪'} {user?.role}
            </span>
          </div>
        </div>

      {error && (
        <div className="alert alert-danger">
          {error}
          <div className="mt-2">
            <button 
              onClick={() => fetchDashboardStats()} 
              className="btn btn-sm btn-outline"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      <div className="dashboard-stats">
        <div className="stats-grid">
          <StatCard
            title="Total Issues"
            value={stats.total_issues}
            color="blue"
            icon="📋"
            link="/issues"
          />
          <StatCard
            title="Open Issues"
            value={stats.open_issues}
            color="green"
            icon="📄"
            link="/issues?status=Open"
          />
          <StatCard
            title="In Progress"
            value={stats.in_progress_issues}
            color="yellow"
            icon="⚡"
            link="/issues?status=In Progress"
          />
          <StatCard
            title="Closed Issues"
            value={stats.closed_issues}
            color="gray"
            icon="✅"
            link="/issues?status=Closed"
          />
          <StatCard
            title="High Priority"
            value={stats.high_priority_issues}
            color="orange"
            icon="⚠️"
            link="/issues?priority=High"
          />
          <StatCard
            title="Critical Priority"
            value={stats.critical_priority_issues}
            color="red"
            icon="🚨"
            link="/issues?priority=Critical"
          />
        </div>
      </div>

      <div className="dashboard-actions">
        <h2>Quick Actions</h2>
        <div className="action-buttons">
          <Link to="/create-issue" className="btn btn-primary">
            Create New Issue
          </Link>
          <Link to="/issues" className="btn btn-secondary">
            View All Issues
          </Link>
          <Link to="/activities" className="btn btn-outline">
            Recent Activities
          </Link>
        </div>
      </div>

      <div className="dashboard-recent">
        <h2>Recent Activities</h2>
        {stats.recent_activities.length > 0 ? (
          <div className="activity-list">
            {stats.recent_activities.slice(0, 5).map((activity) => (
              <div key={activity.id} className="activity-item">
                <div className="activity-content">
                  <span className="activity-user">{activity.user_name}</span>
                  <span className="activity-action">{activity.action}</span>
                  <span className="activity-description">{activity.description}</span>
                </div>
                <div className="activity-time">
                  {new Date(activity.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
            <Link to="/activities" className="view-all-activities">
              View All Activities →
            </Link>
          </div>
        ) : (
          <p className="no-activities">No recent activities to display.</p>
        )}
      </div>
      </div>
    </div>
  );
};

export default Dashboard; 